import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../../config";
import { SPECIALTIES } from "../../SpecialtyContext";

const CHUNK = 512 * 1024;

/** Сжимаем в браузере: JSON с историей чата сжимается в 8–10 раз, а сервер принимает тело не больше 1 МБ за запрос. */
async function gzipBlob(file) {
  if (typeof CompressionStream === "undefined") return file;
  const stream = file.stream().pipeThrough(new CompressionStream("gzip"));
  return await new Response(stream).blob();
}

const fmtDate = iso => (iso ? new Date(iso + "Z").toLocaleDateString("ru-RU") : "—");

export default function ChatImportTab({ token }) {
  const auth = { Authorization: `Bearer ${token}` };
  const fileRef = useRef(null);
  const [stage, setStage] = useState("pick");   // pick | uploading | analyzed | importing | done
  const [progress, setProgress] = useState("");
  const [uid, setUid] = useState(null);
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);          // выбор тем: {key,title,count,include,label}
  const [anonymize, setAnonymize] = useState(false);
  const [specialty, setSpecialty] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(() => {
    fetch(`${API_URL}/admin/chat-import/batches`, { headers: auth }).then(r => (r.ok ? r.json() : [])).then(setBatches).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const fail = msg => { setError(msg); setStage("pick"); setProgress(""); };
  const detail = async r => { const d = await r.json().catch(() => ({})); return typeof d.detail === "string" ? d.detail : "Ошибка запроса"; };

  const onFile = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError(""); setResult(null); setStage("uploading");
    try {
      setProgress("Сжимаем файл…");
      const blob = await gzipBlob(file);
      const r0 = await fetch(`${API_URL}/admin/chat-import/uploads`, { method: "POST", headers: auth });
      if (!r0.ok) return fail(await detail(r0));
      const { upload_id } = await r0.json();
      for (let off = 0; off < blob.size; off += CHUNK) {
        setProgress(`Загрузка: ${Math.min(100, Math.round((off / blob.size) * 100))}%`);
        const r = await fetch(`${API_URL}/admin/chat-import/uploads/${upload_id}/chunk`, {
          method: "POST", headers: { ...auth, "Content-Type": "application/octet-stream" }, body: blob.slice(off, off + CHUNK),
        });
        if (!r.ok) return fail(await detail(r));
      }
      setProgress("Разбираем файл…");
      const ra = await fetch(`${API_URL}/admin/chat-import/uploads/${upload_id}/analyze`, { method: "POST", headers: auth });
      if (!ra.ok) return fail(await detail(ra));
      const data = await ra.json();
      setUid(upload_id); setInfo(data);
      setRows(data.topics.map(t => ({ ...t, include: true, label: t.title })));
      setStage("analyzed"); setProgress("");
    } catch (err) {
      fail("Не удалось загрузить файл. Проверьте соединение и попробуйте ещё раз.");
    }
  };

  const start = async () => {
    setError(""); setStage("importing"); setProgress("Переносим сообщения…");
    const body = { anonymize, specialty: specialty || null, topics: rows.map(r => ({ key: r.key, include: r.include, label: r.label })) };
    const r = await fetch(`${API_URL}/admin/chat-import/uploads/${uid}/commit`, { method: "POST", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!r.ok) { setError(await detail(r)); setStage("analyzed"); return; }
    const { job_id } = await r.json();
    const timer = setInterval(async () => {
      const jr = await fetch(`${API_URL}/admin/chat-import/jobs/${job_id}`, { headers: auth });
      if (!jr.ok) { clearInterval(timer); setError(await detail(jr)); setStage("pick"); loadBatches(); return; }
      const j = await jr.json();
      if (j.state === "running") { setProgress(`Перенесено ${j.done} из ${j.total}`); return; }
      clearInterval(timer);
      if (j.state === "error") { setError(`Перенос остановился: ${j.error}. Уже загруженное можно откатить ниже.`); setStage("pick"); }
      else { setResult(j); setStage("done"); }
      setProgress(""); loadBatches();
    }, 1500);
  };

  const rollback = async b => {
    if (!window.confirm(`Удалить все сообщения этого переноса (${b.left})? Созданные для него каналы без сообщений тоже удалятся.`)) return;
    const r = await fetch(`${API_URL}/admin/chat-import/batches/${b.id}/rollback`, { method: "POST", headers: auth });
    if (!r.ok) setError(await detail(r));
    loadBatches();
  };

  const reset = () => { setStage("pick"); setInfo(null); setRows([]); setUid(null); setResult(null); setError(""); };
  const setRow = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const chosen = rows.filter(r => r.include).reduce((n, r) => n + r.count, 0);

  return (
    <div className="admin-section">
      <h2>Импорт чата из Telegram</h2>
      <p style={{ color: "#5b6773", maxWidth: 760, lineHeight: 1.5 }}>
        В Telegram Desktop откройте нужную группу → меню «⋮» → «Экспорт истории чата», формат <b>JSON</b> (медиа можно не отмечать).
        Загрузите файл <b>result.json</b>. Темы группы станут каналами. Файлы и фото не переносятся: в тексте остаётся пометка «[Фото]».
        Перенесённые сообщения видны только вошедшим пользователям.
      </p>
      {error && <div className="auth-error" style={{ margin: "10px 0" }}>{error}</div>}

      {(stage === "pick") && (
        <div>
          <input ref={fileRef} type="file" accept=".json,application/json" onChange={onFile} style={{ display: "none" }} />
          <button type="button" className="auth-submit-btn" style={{ width: "auto", padding: "10px 22px" }} onClick={() => fileRef.current.click()}>Выбрать файл экспорта</button>
        </div>
      )}

      {stage === "uploading" && <p>{progress}</p>}

      {stage === "analyzed" && info && (
        <div>
          <p><b>{info.chat_name}</b>: {info.total} сообщений от {info.authors} участников, {fmtDate(info.first)} — {fmtDate(info.last)}.</p>
          <p style={{ color: "#5b6773" }}>Больше всего пишут: {info.top_authors.slice(0, 5).map(a => `${a.name} (${a.count})`).join(", ")}.</p>
          <table className="admin-table" style={{ margin: "10px 0" }}>
            <thead><tr><th>Переносить</th><th>Тема в Telegram</th><th>Сообщений</th><th>Название канала на портале</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key}>
                  <td><input type="checkbox" checked={r.include} onChange={e => setRow(r.key, { include: e.target.checked })} /></td>
                  <td>{r.title}</td><td>{r.count}</td>
                  <td><input value={r.label} maxLength={80} disabled={!r.include} onChange={e => setRow(r.key, { label: e.target.value })} style={{ width: "100%" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <label style={{ display: "block", margin: "8px 0" }}>
            <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} /> Скрыть имена авторов («Участник 1», «Участник 2»…)
          </label>
          <label style={{ display: "block", margin: "8px 0" }}>
            Специальность каналов (необязательно):{" "}
            <select value={specialty} onChange={e => setSpecialty(e.target.value)}>
              <option value="">Для всех специальностей</option>
              {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <p style={{ color: "#8a5a00", fontSize: "0.88rem", maxWidth: 760 }}>
            Перед запуском убедитесь, что участники чата предупреждены о переносе, а в истории нет данных пациентов. Перенос можно отменить кнопкой в списке ниже.
          </p>
          <button type="button" className="auth-submit-btn" style={{ width: "auto", padding: "10px 22px" }} disabled={!chosen} onClick={start}>Перенести {chosen} сообщений</button>{" "}
          <button type="button" className="admin-tab-btn" onClick={reset}>Отмена</button>
        </div>
      )}

      {stage === "importing" && <p>{progress}</p>}

      {stage === "done" && result && (
        <div>
          <p style={{ color: "#15803d", fontWeight: 600 }}>Готово: перенесено {result.imported}{result.skipped ? `, уже были раньше: ${result.skipped}` : ""}.</p>
          <button type="button" className="admin-tab-btn" onClick={reset}>Загрузить ещё</button>
        </div>
      )}

      <h3 style={{ marginTop: 28 }}>Выполненные переносы</h3>
      {batches.length === 0 ? <p style={{ color: "#5b6773" }}>Пока нет.</p> : (
        <table className="admin-table">
          <thead><tr><th>Чат</th><th>Когда</th><th>Каналы</th><th>Сообщений</th><th /></tr></thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id}>
                <td>{b.chat_name}{b.anonymized ? " (без имён)" : ""}</td>
                <td>{fmtDate(b.created_at)}</td>
                <td>{b.channels.map(c => `${c.label} (${c.count})`).join(", ")}</td>
                <td>{b.rolled_back ? "отменён" : b.left}</td>
                <td>{!b.rolled_back && b.left > 0 && <button type="button" className="admin-tab-btn" onClick={() => rollback(b)}>Отменить перенос</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
